import { Copy, Edit, MoreVertical, Play, Trash2 } from 'lucide-react'
import { SelectButton } from '@/components/ui/SelectButton'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import type { Play as PlayType } from './Types'
import './play-list-view.css'

type PlayListViewProps = {
	plays: PlayType[]
	selectedPlays: Set<string>

	onSelect: (id: string) => void
	onSelectAllVisible?: () => void
	visiblePlayIds?: string[]

	onOpen: (id: string) => void

	onAnimate?: (id: string) => void

	onRename: (id: string) => void

	onDelete: (id: string) => void

	onDuplicate: (id: string) => void
}

export function PlayListView({
								 plays,
								 selectedPlays,
								 onSelect,
								 onSelectAllVisible,
								 visiblePlayIds,
								 onOpen,
								 onAnimate,
								 onRename,
								 onDelete,
								 onDuplicate,
							 }: PlayListViewProps) {
	const visibleIds = visiblePlayIds ?? plays.map(p => p.id)
	const selectedCount = visibleIds.filter(id => selectedPlays.has(id)).length
	const allSelected = selectedCount === visibleIds.length && visibleIds.length > 0
	const someSelected = selectedCount > 0 && !allSelected

	return (
		<div className="play-list-view">
			<div className="play-list-view-header">
				<div className="play-list-view-header-cell" style={{ gridColumn: 'span 1' }}>
					{onSelectAllVisible && (
						<SelectButton
							selected={allSelected}
							partial={someSelected}
							onClick={onSelectAllVisible}
						/>
					)}
				</div>
				<div className="play-list-view-header-cell" style={{ gridColumn: 'span 5' }}>
					Play
				</div>
				<div className="play-list-view-header-cell" style={{ gridColumn: 'span 2' }}>
					Formation
				</div>
				<div className="play-list-view-header-cell" style={{ gridColumn: 'span 3' }}>
					Labels
				</div>
				<div className="play-list-view-header-cell" style={{ gridColumn: 'span 1' }}></div>
			</div>

			<div className="play-list-view-rows">
				{plays.map((play) => {
					const isSelected = selectedPlays.has(play.id)

					return (
						<div
							key={play.id}
							className={`play-list-view-row ${isSelected ? 'play-list-view-row-selected' : ''}`.trim()}
						>
							<div className="play-list-view-cell" style={{ gridColumn: 'span 1' }}>
								<SelectButton
									selected={isSelected}
									onClick={() => onSelect(play.id)}
								/>
							</div>

							<div className="play-list-view-cell" style={{ gridColumn: 'span 5' }}>
								<button
									onClick={() => onOpen(play.id)}
									className="play-list-view-name-button"
								>
									{play.name}
								</button>
							</div>

							<div className="play-list-view-cell" style={{ gridColumn: 'span 2' }}>
                <span className="play-list-view-formation">
                  {play.formation || '-'}
                </span>
							</div>

							<div className="play-list-view-cell" style={{ gridColumn: 'span 3' }}>
								<div className="play-list-view-labels">
									{play.labelObjects && play.labelObjects.length > 0 ? (
										play.labelObjects.map((label) => (
											<span
												key={label.id}
												className="play-list-view-label"
												style={{
													backgroundColor: `${label.color}20`,
													color: label.color,
													borderColor: label.color,
												}}
											>
												{label.name}
											</span>
										))
									) : (
										<span className="play-list-view-formation">-</span>
									)}
								</div>
							</div>

							<div className="play-list-view-cell play-list-view-actions"
								 style={{ gridColumn: 'span 1' }}>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button className="play-list-view-menu-button">
											<MoreVertical />
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										{onAnimate && (
											<DropdownMenuItem onClick={() => onAnimate(play.id)}>
												<Play />
												Animate
											</DropdownMenuItem>
										)}
										<DropdownMenuItem onClick={() => onRename(play.id)}>
											<Edit />
											Rename
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => onDuplicate(play.id)}>
											<Copy />
											Duplicate
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<DropdownMenuItem onClick={() => onDelete(play.id)} variant="destructive">
											<Trash2 />
											Delete
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}
